import {
  AppointmentType,
  AppointmentTypeUpdateParams,
  Attachment,
  AttachmentCreateParams,
  Program,
} from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import { default as React } from 'react'
import { AttachmentDropzone } from '../../../../../../components/AttachmentDropzone/AttachmentDropzone'
import { Checkbox } from '../../../../../../components/Checkbox/Checkbox'
import { EventTagSelect } from '../../../../../../components/EventTagSelect/EventTagSelect'
import { FeatureFlag } from '../../../../../../components/FeatureFlag/FeatureFlag'
import { FieldsetFlag } from '../../../../../../components/FeatureFlag/components/FieldsetFlag'
import { isEventTypeValid } from '../../../../../../components/FeatureFlag/utils/isEventTypeValid'
import { LoadingButton } from '../../../../../../components/LoadingButton/LoadingButton'
import { MediaSelect } from '../../../../../../components/MediaSelect/MediaSelect'
import { MembershipPackMultiSelect } from '../../../../../../components/MembershipPackSelect.tsx/MembershipPackMultiSelect'
import { CheckoutEditor } from '../../../../../../components/RichTextEditor/CheckoutEditor'
import { WaiverEditor } from '../../../../../../components/RichTextEditor/WaiverEditor'
import { SuggestTextInput } from '../../../../../../components/SuggestTextInput/SuggestTextInput'
import { AppointmentTypeExtended } from '../../hooks/useFetchAppointmentType'
import { AppointmentTypeSchema } from '../../schemas/AppointmentTypeSchema'

export const MaxSummaryLength: number = 140

interface IProps {
  appointmentType: AppointmentType
  program: Program
  attachments: Attachment[]
  onSubmit: (value: IValue) => Promise<AppointmentTypeExtended>
}

type FormFields =
  | 'summary'
  | 'description'
  | 'picture_file_id'
  | 'terms'
  | 'restrict_to_package_ids'
  | 'tags'

interface IValue extends Pick<AppointmentTypeUpdateParams, FormFields> {
  members_only: boolean
  attachments: Array<Attachment | AttachmentCreateParams>
}

export const AppointmentTypeDescriptionForm = ({
  appointmentType,
  attachments: initialAttachments,
  program,
  onSubmit,
}: IProps): React.ReactElement => {
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    isSubmitting,
  } = useFormik<IValue>({
    initialValues: {
      summary: appointmentType.summary || '',
      description: appointmentType.description || '',
      picture_file_id: appointmentType.picture?.id || undefined,
      terms: appointmentType.terms || '',
      restrict_to_package_ids: appointmentType.restrict_to_package_ids || [],
      members_only: appointmentType.restrict_to_package_ids?.length > 0 || false,
      tags: appointmentType.tags || [],
      attachments: initialAttachments || [],
    },
    validationSchema: AppointmentTypeSchema.getPartial('description'),
    onSubmit: async (value, { setSubmitting, setValues }) => {
      const updateType = await onSubmit(value)
      setValues({
        summary: updateType.summary || '',
        description: updateType.description || '',
        picture_file_id: updateType.picture?.id || undefined,
        terms: updateType.terms || '',
        restrict_to_package_ids: updateType.restrict_to_package_ids || [],
        members_only: updateType.restrict_to_package_ids?.length > 0 || false,
        tags: updateType.tags || [],
        attachments: updateType.attachments || [],
      })
      setSubmitting(false)
    },
  })

  return (
    <form className="my-4" onSubmit={handleSubmit}>
      <h4 className="mb-4">Provide details about your appointment</h4>
      <FieldsetFlag
        flagKey={['sessions_enabled']}
        checkAll={(flags) => isEventTypeValid(appointmentType.type, flags)}
      >
        <div className="mb-3">
          <label>Summary</label>
          <SuggestTextInput
            suggestion={{
              program_id: program.id,
              suggestion_type: 'summary',
            }}
            tooltipText="Suggest a summary for your appointment type."
            name="summary"
            type="text"
            className={`form-control ${touched.summary && !!errors.summary ? ' is-invalid' : ''}`}
            value={values.summary}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            maxLength={MaxSummaryLength}
          />
          <div className="invalid-feedback d-block">
            <div className="d-flex justify-content-between">
              <div>{touched.summary && !!errors.summary ? errors.summary : '\u00A0'}</div>
              <div className="text-body-secondary">
                {(values.summary || '').length}/{MaxSummaryLength}
              </div>
            </div>
          </div>
        </div>
        <div className="mb-3">
          <label className="optional">Description</label>
          <small className="form-text mb-1">
            Write a description for what your appointment is about.
          </small>
          <CheckoutEditor
            name="description"
            programId={program.id}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <div className="invalid-feedback d-block">
            {touched?.description && !!errors?.description ? errors?.description : '\u00A0'}
          </div>
        </div>
        <div className="mb-3">
          <label className="optional">Waiver</label>
          <small className="form-text mb-1">
            Write terms that the attendees must agree to attend the appointment.
          </small>
          <WaiverEditor
            name="terms"
            value={values.terms || ''}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <div className="invalid-feedback d-block">
            {touched?.terms && !!errors?.terms ? errors?.terms : '\u00A0'}
          </div>
        </div>
        <div className="mb-3">
          <label className="optional">Appointment Image</label>
          <small className="form-text mb-1">
            Choose an image that captures your appointment. Recommended size: 2160 x 1080px
          </small>
          <MediaSelect
            value={values?.picture_file_id}
            programId={
              typeof appointmentType.program === 'string'
                ? appointmentType.program
                : appointmentType.program.id
            }
            accept={['image/png', 'image/jpeg', 'image/gif']}
            purpose="event_picture"
            onChange={(file): void => {
              setFieldValue('picture_file_id', file?.id || null, true)
            }}
          />
          <div className="invalid-feedback d-block">
            {touched?.picture_file_id && !!errors?.picture_file_id
              ? errors?.picture_file_id
              : '\u00A0'}
          </div>
        </div>
        <h4 className="mb-4">Provide additional information about your appointment</h4>
        <FeatureFlag flagKey="memberships_enabled">
          <div className="mb-3">
            <Checkbox
              name="is_members_only"
              id="is_members_only"
              className="mb-2"
              checked={values.members_only}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                if (evt.target.checked) {
                  setFieldValue('members_only', true, true)
                } else {
                  setFieldValue('members_only', false, true)
                  setFieldValue('restrict_to_package_ids', [], true)
                }
              }}
              onBlur={handleBlur}
            >
              <div>Customers with memberships</div>
              <div className="text-body-secondary small">
                Only attendees with the selected memberships can register for the appointment
              </div>
            </Checkbox>
            {values.members_only && (
              <div className="ms-3 ps-3">
                <MembershipPackMultiSelect
                  values={values.restrict_to_package_ids || []}
                  programId={program.id}
                  hasError={touched?.restrict_to_package_ids && !!errors?.restrict_to_package_ids}
                  onSelect={(memberPackages) => {
                    const val = memberPackages.map((v) => v.id)
                    if ((val || []).length > (values.restrict_to_package_ids || [])?.length) {
                      const numValidPacks = (val || []).filter(
                        (id) => !values.restrict_to_package_ids?.includes(id),
                      ).length
                      if (numValidPacks === 1) {
                        setFieldValue('restrict_to_package_ids', val, true)
                      }
                    } else {
                      setFieldValue('restrict_to_package_ids', val, true)
                    }
                  }}
                  onBlur={handleBlur}
                />
                <div className="invalid-feedback d-block">
                  {touched?.restrict_to_package_ids && !!errors?.restrict_to_package_ids
                    ? errors?.restrict_to_package_ids
                    : '\u00A0'}
                </div>
              </div>
            )}
          </div>
        </FeatureFlag>
        <div className="mb-3">
          <label className="optional">Tags</label>
          <EventTagSelect
            inputId="tags"
            placeholder="Add tags..."
            programId={
              typeof appointmentType.program === 'string'
                ? appointmentType.program
                : appointmentType.program.id
            }
            onChange={(val: string[]) => {
              setFieldValue('tags', val, true)
            }}
            values={values.tags}
          />
          <div className="invalid-feedback d-block">
            {touched?.tags && !!errors?.tags ? errors?.tags : '\u00A0'}
          </div>
        </div>
        <div className="mb-3">
          <label className="optional">Attachment</label>
          <small className="form-text mb-1">
            Add any documents you wish to share with the attendees such as schedule, curriculum,
            etc.
          </small>
          <AttachmentDropzone
            icon="file"
            purpose="event_attachment"
            programId={program.id}
            parentId={appointmentType.id}
            attachmentType="appointment_type"
            uploadOnDrop={true}
            values={values.attachments}
            onChange={(attachments) => {
              setFieldValue('attachments', attachments, true)
            }}
          >
            Drag your document here, or click to upload them
          </AttachmentDropzone>
          <div className="invalid-feedback d-block">
            {touched?.attachments &&
            !!errors?.attachments &&
            typeof errors?.attachments === 'string'
              ? errors?.attachments
              : '\u00A0'}
          </div>
        </div>
        <div className="row">
          <div className="mb-3 col d-flex justify-content-between mt-4">
            <LoadingButton
              type="submit"
              className="btn btn-primary btn-block d-md-inline-block w-md-auto"
              loading={isSubmitting}
            >
              Save Changes
            </LoadingButton>
          </div>
        </div>
      </FieldsetFlag>
    </form>
  )
}
